import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, Network, Sparkles, Users, Megaphone, ArrowRight } from 'lucide-react';
import { SectionHeader, CompanyAvatar } from '@/components/common/Primitives';
import { useData } from '@/hooks/useData';
import { useSeo } from '@/lib/seo';
import { relativeDate } from '@/lib/format';

const Panel = ({ icon: Icon, title, note, children, testId }) => (
  <section className="surface p-5" data-testid={testId}>
    <div className="mb-4 flex items-center justify-between gap-2">
      <h2 className="flex items-center gap-2 font-display text-base font-semibold"><Icon className="h-4 w-4 text-primary" /> {title}</h2>
      <span className="rounded border border-white/10 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">PRÉVIA</span>
    </div>
    {children}
    {note && <p className="mt-4 text-[11px] text-muted-foreground">{note}</p>}
  </section>
);

export default function Community() {
  useSeo({ title: 'Comunidade', description: 'Recursos comunitários da rede Brasil Livre Business.' });
  const { publicCompanies, categoryById } = useData();
  const newest = useMemo(() => [...publicCompanies].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5), [publicCompanies]);
  const byCat = useMemo(() => {
    const m = {};
    publicCompanies.forEach((c) => { (m[c.categoryId] = m[c.categoryId] || []).push(c); });
    return Object.entries(m).filter(([, v]) => v.length > 1).slice(0, 3);
  }, [publicCompanies]);
  const highlights = publicCompanies.filter((c) => c.plan === 'premium').slice(0, 3);

  return (
    <div className="container-x py-10 sm:py-14">
      <SectionHeader eyebrow="Em construção" title="Comunidade e indicações" text="Área preparada para recursos comunitários futuros. Nesta versão, não há feed social, mensagens entre perfis nem sistema de avaliações. As sugestões abaixo são geradas automaticamente, não por pessoas." />
      <div className="grid gap-5 lg:grid-cols-3">
        <Panel icon={Star} title="Empresas recomendadas" note="Sugestões automáticas por categoria (sem avaliações de usuários)." testId="community-recommended">
          <div className="space-y-4">
            {byCat.length ? byCat.map(([cat, list]) => (
              <div key={cat}>
                <div className="mb-1.5 text-xs text-muted-foreground">{categoryById[cat]?.name}</div>
                {list.slice(0, 2).map((c) => (
                  <Link key={c.id} to={`/empresas/${c.id}`} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-white/5"><CompanyAvatar company={c} size={28} /><span className="truncate text-sm">{c.name}</span></Link>
                ))}
              </div>
            )) : <p className="text-sm text-muted-foreground">As sugestões aparecem quando houver mais empresas em uma mesma categoria.</p>}
          </div>
        </Panel>
        <Panel icon={Network} title="Novas conexões comerciais" note="Empresas que entraram na rede recentemente." testId="community-new">
          {newest.length ? (
            <ul className="space-y-1">
              {newest.map((c) => (
                <li key={c.id}><Link to={`/empresas/${c.id}`} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-white/5"><CompanyAvatar company={c} size={28} /><span className="min-w-0 flex-1 truncate text-sm">{c.name}</span><span className="font-mono text-[11px] text-muted-foreground">{relativeDate(c.createdAt)}</span></Link></li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">Nenhuma empresa cadastrada ainda.</p>}
        </Panel>
        <Panel icon={Sparkles} title="Destaques empresariais" note="Futuro: curadoria editorial identificada." testId="community-highlights">
          {highlights.length ? (
            <ul className="space-y-1">
              {highlights.map((c) => (
                <li key={c.id}><Link to={`/empresas/${c.id}`} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-white/5"><CompanyAvatar company={c} size={28} /><span className="truncate text-sm">{c.name}</span><ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" /></Link></li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">Os destaques serão exibidos quando houver perfis em destaque.</p>}
        </Panel>
        <Panel icon={Users} title="Indicações de prestadores" note="Futuro: empresas poderão indicar prestadores com quem já trabalharam." testId="community-referrals">
          <p className="text-sm text-muted-foreground">Nenhuma indicação ainda. Este recurso será liberado em uma próxima etapa.</p>
        </Panel>
        <Panel icon={Megaphone} title="Publicações institucionais" note="Comunicados da plataforma." testId="community-institutional">
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 text-sm">
            <div className="font-medium">Bem-vindo à Brasil Livre Business</div>
            <p className="mt-1 text-muted-foreground">Cadastre sua empresa, publique oportunidades e encontre parceiros comerciais em todo o país.</p>
          </div>
        </Panel>
      </div>
    </div>
  );
}